import React, { useMemo, useState } from 'react';
import { getISTMidnightTimestamp } from '../hooks/useFirebaseData';

// Same exclude-refunded + amount-fallback logic as useFirebaseData's own
// all-time aggregate, applied here per-scope (today vs all-time) instead.
function aggregate(orders, refundedIds) {
  let revenue = 0;
  let cups = 0;
  orders.forEach((order) => {
    if (refundedIds.has(order.id)) return;
    if (order.amount) revenue += Number(order.amount);
    if (order.item_id) cups += 1;
    if (!order.amount && order.status === 'PAID') {
      revenue += 19;
      cups += 1;
    }
  });
  return { revenue, cups };
}

export default function KPICards({ orders, refundedIds, inventory }) {
  const [scope, setScope] = useState('today'); // 'today' | 'all'

  const totalKiosks = Object.keys(inventory).length;

  const todayOrders = useMemo(() => {
    const cutoff = getISTMidnightTimestamp();
    return orders.filter((o) => (o.timestamp || 0) >= cutoff);
  }, [orders]);

  const { revenue, cups } = useMemo(() => {
    const scoped = scope === 'today' ? todayOrders : orders;
    return aggregate(scoped, refundedIds);
  }, [scope, todayOrders, orders, refundedIds]);

  const avgOrder = cups > 0 ? Math.round(revenue / cups) : 0;

  const cards = [
    {
      id: 'kpi-cups',
      label: 'Cups Dispensed',
      value: cups.toLocaleString('en-IN'),
      unit: 'cups',
      icon: '🥤',
      color: 'var(--accent-blue)',
      bg: 'var(--accent-blue-glow)',
    },
    {
      id: 'kpi-revenue',
      label: 'Gross Revenue',
      value: `₹${revenue.toLocaleString('en-IN')}`,
      unit: '',
      icon: '💰',
      color: 'var(--accent-green)',
      bg: 'var(--accent-green-glow)',
    },
    {
      id: 'kpi-kiosks',
      label: 'Active Kiosks',
      value: totalKiosks,
      unit: 'online',
      icon: '📍',
      color: 'var(--accent-purple)',
      bg: 'var(--accent-purple-glow)',
    },
    {
      id: 'kpi-avg',
      label: 'Avg. Order Value',
      value: `₹${avgOrder}`,
      unit: '',
      icon: '📊',
      color: 'var(--accent-amber)',
      bg: 'var(--accent-amber-glow)',
    },
  ];

  return (
    <div>
      <div className="date-filter-group" style={{ marginBottom: '0.75rem', display: 'inline-flex' }}>
        <button
          id="kpi-scope-today"
          className={`date-filter-btn ${scope === 'today' ? 'date-filter-btn--active' : ''}`}
          onClick={() => setScope('today')}
        >
          Today
        </button>
        <button
          id="kpi-scope-all"
          className={`date-filter-btn ${scope === 'all' ? 'date-filter-btn--active' : ''}`}
          onClick={() => setScope('all')}
        >
          All Time
        </button>
      </div>

      <div className="kpi-grid">
        {cards.map((card) => (
          <div key={card.id} id={card.id} className="kpi-card">
            <div className="kpi-card-header">
              <span className="kpi-card-icon" style={{ backgroundColor: card.bg, color: card.color }}>
                {card.icon}
              </span>
              <span className="kpi-card-label">{card.label}</span>
            </div>
            <div className="kpi-card-value" style={{ color: card.color }}>
              {card.value}
              {card.unit && <span className="kpi-card-unit">{card.unit}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
